import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Star, MapPin, Loader2, ImageOff } from 'lucide-react';
import { publicApi, resolveImage } from '../api';
import { useLang } from '../i18n';

/**
 * HomeFeaturedCarousel — "Esperienze da non perdere"
 *
 * Horizontal snap-scrolling carousel of featured activities.
 * - Autoscroll every 5s (pauses on hover/touch)
 * - Manual scroll with left/right arrows
 * - Data source: GET /api/public/home-featured (max 8 items)
 * - Cards link to the full activity detail page: /:citySlug/attivita/:id
 *
 * Managed from Admin → Attività → toggle "In vetrina Home".
 */
export default function HomeFeaturedCarousel() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [brokenImages, setBrokenImages] = useState({});
  const scrollerRef = useRef(null);
  const pausedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    publicApi.homeFeatured(8)
      .then((data) => { if (!cancelled) setItems(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Autoscroll every 5s (pauses on user hover/touch)
  useEffect(() => {
    if (items.length <= 1) return;
    const id = setInterval(() => {
      const el = scrollerRef.current;
      if (!el || pausedRef.current) return;
      const cardWidth = el.firstChild?.getBoundingClientRect().width || 320;
      // If near the end, jump back to the start
      if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 24) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: cardWidth + 20, behavior: 'smooth' });
      }
    }, 5000);
    return () => clearInterval(id);
  }, [items.length]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const cardWidth = el.firstChild?.getBoundingClientRect().width || 320;
    el.scrollBy({ left: dir * (cardWidth + 20), behavior: 'smooth' });
  };

  const rating = (a) => a.google_rating ?? a.rating ?? null;
  const ratingCount = (a) => a.google_review_count ?? a.review_count ?? 0;

  if (!loading && items.length === 0) return null;

  return (
    <section
      className="relative w-full py-14 sm:py-20"
      data-testid="home-featured-section"
      style={{
        background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(188,172,138,0.05) 40%, rgba(0,0,0,0) 100%)'
      }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Heading — centered on mobile, split on desktop */}
        <div className="px-4 sm:px-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div className="text-center md:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[color:var(--yup-primary)]/40 bg-[color:var(--yup-primary)]/10 text-[color:var(--yup-primary)] text-[10px] tracking-[0.3em] uppercase mb-3">
              <Star size={12} />
              <span>YUP</span>
            </div>
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="home-featured-title"
            >
              {t('featuredTitle')}
            </h2>
            <p className="mt-2 text-white/60 text-sm sm:text-base max-w-xl mx-auto md:mx-0">
              {t('featuredSubtitle')}
            </p>
          </div>
          {/* Desktop arrow controls */}
          <div className="hidden md:flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              className="w-11 h-11 rounded-full border border-white/20 hover:border-[color:var(--yup-primary)] hover:bg-[color:var(--yup-primary)]/10 text-white/90 flex items-center justify-center transition-colors"
              aria-label="Scorri a sinistra"
              data-testid="home-featured-prev"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              className="w-11 h-11 rounded-full border border-white/20 hover:border-[color:var(--yup-primary)] hover:bg-[color:var(--yup-primary)]/10 text-white/90 flex items-center justify-center transition-colors"
              aria-label="Scorri a destra"
              data-testid="home-featured-next"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        {loading && (
          <div className="flex justify-center py-10">
            <Loader2 className="animate-spin text-white/40" size={28} />
          </div>
        )}

        {!loading && items.length > 0 && (
          <>
          <div
            ref={scrollerRef}
            onMouseEnter={() => { pausedRef.current = true; }}
            onMouseLeave={() => { pausedRef.current = false; }}
            onTouchStart={() => { pausedRef.current = true; }}
            onTouchEnd={() => { setTimeout(() => { pausedRef.current = false; }, 3000); }}
            onScroll={(e) => {
              const el = e.currentTarget;
              const cardWidth = el.firstChild?.getBoundingClientRect().width || 300;
              const idx = Math.round(el.scrollLeft / (cardWidth + 16));
              if (idx !== activeIdx) setActiveIdx(idx);
            }}
            className="flex gap-4 sm:gap-5 overflow-x-auto snap-x snap-mandatory pb-4 scrollbar-none pl-4 pr-16 sm:pl-6 sm:pr-6"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', scrollPaddingLeft: '1rem' }}
            data-testid="home-featured-scroller"
          >
            {items.map((a) => {
              const r = rating(a);
              const catPath = a.subcategory
                ? `${a.category_key}/${a.subcategory}`
                : a.category_key;
              const linkTo = `/${a.city_slug}/cosa-vedere/${catPath}#act-${a.id}`;
              const shortDesc = (a.description?.[lang] || a.description?.it || '').slice(0, 90);
              const showImage = a.image && !brokenImages[a.id];
              return (
                <Link
                  key={a.id}
                  to={linkTo}
                  className="group relative snap-start shrink-0 w-[82vw] max-w-[300px] sm:w-[320px] md:w-[340px] overflow-hidden rounded-2xl border border-white/10 bg-black/40 hover:border-[color:var(--yup-primary)]/60 transition-all hover:-translate-y-1"
                  data-testid={`home-featured-card-${a.id}`}
                >
                  <div className="relative w-full h-[190px] sm:h-[220px] overflow-hidden bg-gradient-to-br from-[color:var(--yup-primary)]/20 via-black/50 to-black/80">
                    {showImage ? (
                      <img
                        src={resolveImage(a.image)}
                        alt={a.name}
                        loading="lazy"
                        decoding="async"
                        onError={() => setBrokenImages(prev => ({ ...prev, [a.id]: true }))}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageOff size={44} className="text-[color:var(--yup-primary)]/50" strokeWidth={1.2} />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                    {/* Rating badge */}
                    {r != null && (
                      <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-black/70 backdrop-blur text-white text-xs font-semibold">
                        <Star size={12} className="fill-[color:var(--yup-primary)] text-[color:var(--yup-primary)]" />
                        <span>{Number(r).toFixed(1)}</span>
                        {ratingCount(a) > 0 && (
                          <span className="text-white/60">· {ratingCount(a)}</span>
                        )}
                      </div>
                    )}
                    {/* Offer badge */}
                    {a.featured_offer_text && (
                      <div className="absolute top-3 right-3 max-w-[60%] px-2 py-1 rounded-full bg-[color:var(--yup-primary)] text-black text-[10px] font-bold uppercase tracking-wider truncate">
                        {a.featured_offer_text}
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-[color:var(--yup-primary)]/90 mb-1">
                      <MapPin size={11} />
                      <span className="truncate">{a.city_slug} · {a.subcategory || a.category_key}</span>
                    </div>
                    <h3 className="text-white font-semibold text-base leading-tight mb-1 line-clamp-2 group-hover:text-[color:var(--yup-primary)] transition-colors">
                      {a.name}
                    </h3>
                    {shortDesc && (
                      <p className="text-white/55 text-xs line-clamp-2">{shortDesc}</p>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Dot indicators — mobile-focused, hidden when only 1 item */}
          {items.length > 1 && (
            <div className="flex md:hidden justify-center gap-2 mt-4" data-testid="home-featured-dots">
              {items.map((it, i) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => {
                    const el = scrollerRef.current;
                    if (!el) return;
                    const cardWidth = el.firstChild?.getBoundingClientRect().width || 300;
                    el.scrollTo({ left: i * (cardWidth + 16), behavior: 'smooth' });
                  }}
                  aria-label={`Vai all'esperienza ${i + 1}`}
                  className={`h-2 rounded-full transition-all ${i === activeIdx ? 'w-8 bg-[color:var(--yup-primary)]' : 'w-2 bg-white/25 hover:bg-white/50'}`}
                />
              ))}
            </div>
          )}

          {/* Swipe hint — only visible on first render, mobile only */}
          {items.length > 1 && activeIdx === 0 && (
            <p className="md:hidden mt-3 text-center text-white/40 text-xs tracking-[0.25em] uppercase animate-pulse">
              ← Scorri per vedere le altre
            </p>
          )}
          </>
        )}
      </div>
    </section>
  );
}
